import { twx } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const badgeVariants = [
  'primary',
  'info',
  'caution',
  'meta',
  'caution-secondary',
  'info-secondary',
  'meta-secondary',
] as const;
export type BadgeVariant = (typeof badgeVariants)[number];

export const badgeIconVariants = ['success', 'error'] as const;
export type BadgeIconVariant = (typeof badgeIconVariants)[number];

export const badgeSizes = ['sm', 'md', 'lg'] as const;
export type BadgeSize = (typeof badgeSizes)[number];

const badgeBorderClassnamesForVariant = (variant: BadgeVariant) => ({
  'border border-divider-main/25': ['primary', 'info', 'caution'].includes(variant),
});

const badgeBackgroundClassnamesForVariant = (variant: BadgeVariant) => ({
  'bg-success-background': variant === 'primary',
  'bg-info-background': variant === 'info',
  'bg-caution-background': variant === 'caution',
  'bg-meta-background': variant === 'meta',
});

const badgeTextClassnamesForVariant = (variant: BadgeVariant) => ({
  'text-success-main': variant === 'primary',
  'text-info-main': ['info', 'info-secondary'].includes(variant),
  'text-caution-main': ['caution', 'caution-secondary'].includes(variant),
  'text-meta-text': ['meta', 'meta-secondary'].includes(variant),
});

const badgeIconClassnamesForIconVariant = (iconVariant: BadgeIconVariant) => ({
  'text-success-main': iconVariant === 'success',
  'text-error-main': iconVariant === 'error',
});

export type BadgeProps = {
  className?: string;
  variant: BadgeVariant;
  iconVariant?: BadgeIconVariant;
  size?: BadgeSize;
  content: string;
  icon?: IconDefinition;
  testId?: string;
};

export const Badge = ({
  className,
  variant,
  iconVariant,
  size = 'sm',
  content,
  icon,
  testId,
}: BadgeProps) => (
  <div
    className={twx(
      'flex max-h-fit max-w-fit items-center justify-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-full',
      badgeBorderClassnamesForVariant(variant),
      badgeBackgroundClassnamesForVariant(variant),
      badgeTextClassnamesForVariant(variant),
      {
        'px-2 py-0.5': size === 'sm',
        'px-2 py-1': size === 'md',
        'px-2.5 py-1.5': size === 'lg',
      },
      className,
    )}
    data-testid={testId}
  >
    {icon && (
      <FontAwesomeIcon
        icon={icon}
        className={twx(
          'h-3 w-3',
          iconVariant
            ? badgeIconClassnamesForIconVariant(iconVariant)
            : badgeTextClassnamesForVariant(variant),
        )}
      />
    )}
    <div
      className={twx('font-medium uppercase', {
        'text-xs': ['sm', 'md'].includes(size),
        'text-sm': size === 'lg',
      })}
    >
      {content}
    </div>
  </div>
);
